import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

/**
 * Export Service
 * Handles CSV and PDF export functionality with CloudSafe branding
 */

/**
 * Convert array of objects to CSV string
 */
const arrayToCSV = (data, headers = null) => {
    if (!data || data.length === 0) {
        return '';
    }

    // Get headers from first object if not provided
    const csvHeaders = headers || Object.keys(data[0]);
    
    // Create CSV header row
    const headerRow = csvHeaders.join(',');
    
    // Create CSV data rows
    const dataRows = data.map(row => {
        return csvHeaders.map(header => {
            const value = row[header] || '';
            // Escape quotes and wrap in quotes if contains comma or quote
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        }).join(',');
    });
    
    return [headerRow, ...dataRows].join('\n');
};

/**
 * Download file with given content
 */
const downloadFile = (content, filename, mimeType = 'text/plain') => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Export data as CSV
 */
export const exportToCSV = (data, filename, options = {}) => {
    const {
        headers = null,
        includeTimestamp = true,
        includeMetadata = true
    } = options;

    if (!data || data.length === 0) {
        throw new Error('No data to export');
    }

    let csvContent = '';
    
    // Add metadata if requested
    if (includeMetadata) {
        csvContent += `# CloudSafe Export Report\n`;
        csvContent += `# Generated: ${new Date().toLocaleString()}\n`;
        csvContent += `# Records: ${data.length}\n`;
        csvContent += `\n`;
    }
    
    // Add main data
    csvContent += arrayToCSV(data, headers);
    
    // Create filename with timestamp
    const timestamp = includeTimestamp ? `_${new Date().toISOString().split('T')[0]}` : '';
    const finalFilename = `${filename}${timestamp}.csv`;
    
    downloadFile(csvContent, finalFilename, 'text/csv');
    
    return {
        success: true,
        filename: finalFilename,
        recordCount: data.length
    };
};

/**
 * Export dashboard data as CSV
 */
export const exportDashboardCSV = (properties, filters = {}) => {
    const exportData = properties.map(property => ({
        'Property ID': property.id,
        'Property Name': property.name,
        'Address': property.address,
        'Total Assets': property.totalAssets || 0,
        'Passed Tests': property.passedTests || 0,
        'Failed Tests': property.failedTests || 0,
        'Pending Tests': property.pendingTests || 0,
        'Overdue Tests': property.overdueTests || 0,
        'Never Tested': property.neverTested || 0,
        'Compliance Rate': property.complianceRate ? `${property.complianceRate}%` : 'N/A',
        'Last Updated': property.updatedAt ? new Date(property.updatedAt).toLocaleDateString() : 'N/A'
    }));

    const filterInfo = Object.entries(filters)
        .filter(([key, value]) => value && value !== '')
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');

    const filename = `cloudsafe_dashboard${filterInfo ? '_filtered' : ''}`;
    
    return exportToCSV(exportData, filename, {
        includeMetadata: true
    });
};

/**
 * Export test results as CSV
 */
export const exportTestResultsCSV = (testResults, assetInfo = {}) => {
    const exportData = testResults.map(result => ({
        'Reference ID': result.referenceId,
        'Asset Name': assetInfo[result.referenceId]?.name || 'Unknown',
        'Asset Type': result.assetType,
        'Property ID': result.propertyId,
        'Test Status': result.status,
        'Test Date': new Date(result.testDate).toLocaleDateString(),
        'PDF Report URL': result.pdfUrl,
        'Days Since Test': Math.floor((new Date() - new Date(result.testDate)) / (1000 * 60 * 60 * 24)),
        'Created At': new Date(result.createdAt).toLocaleDateString()
    }));

    return exportToCSV(exportData, 'cloudsafe_test_results', {
        includeMetadata: true
    });
};

/**
 * Create branded PDF header
 */
const addPDFHeader = (doc, title, subtitle = null) => {
    const pageWidth = doc.internal.pageSize.width;
    
    // CloudSafe Logo (text-based for now)
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 115, 220); // CloudSafe blue
    doc.text('CloudSafe', 20, 25);
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 32, 44); // Dark gray
    doc.text(title, 20, 45);
    
    // Subtitle
    if (subtitle) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(113, 128, 150); // Light gray
        doc.text(subtitle, 20, 55);
    }
    
    // Date and time
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(113, 128, 150);
    const now = new Date().toLocaleString();
    doc.text(`Generated: ${now}`, pageWidth - 20, 25, { align: 'right' });
    
    // Separator line
    doc.setDrawColor(226, 232, 240);
    doc.line(20, subtitle ? 65 : 55, pageWidth - 20, subtitle ? 65 : 55);
    
    return subtitle ? 75 : 65;
};

/**
 * Add PDF footer
 */
const addPDFFooter = (doc, pageNumber, totalPages) => {
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    
    // Footer line
    doc.setDrawColor(226, 232, 240);
    doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);
    
    // Page number
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(113, 128, 150);
    doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
    
    // CloudSafe branding
    doc.text('CloudSafe Safety Management System', 20, pageHeight - 10);
};

/**
 * Export dashboard data as branded PDF
 */
export const exportDashboardPDF = (properties, counters = {}, filters = {}) => {
    const doc = new jsPDF();
    
    // Header
    const startY = addPDFHeader(
        doc, 
        'Dashboard Report', 
        'Property and Asset Overview'
    );
    
    let currentY = startY + 10;
    
    // Summary Statistics
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 32, 44);
    doc.text('Summary Statistics', 20, currentY);
    currentY += 15;
    
    const summaryData = [
        ['Total Properties', properties.length.toString()],
        ['Total Assets', (counters.total || 0).toString()],
        ['Passed Tests', (counters.passed || 0).toString()],
        ['Failed Tests', (counters.failed || 0).toString()],
        ['Overdue Tests', (counters.overdue || 0).toString()],
        ['Never Tested', (counters.neverTested || 0).toString()],
        ['Overall Compliance', counters.total > 0 ? `${Math.round((counters.passed / counters.total) * 100)}%` : 'N/A']
    ];
    
    doc.autoTable({
        startY: currentY,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [51, 115, 220], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 20, right: 20 }
    });
    
    currentY = doc.lastAutoTable.finalY + 20;
    
    // Applied Filters (if any)
    const activeFilters = Object.entries(filters)
        .filter(([key, value]) => value && value !== '');
        
    if (activeFilters.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Applied Filters', 20, currentY);
        currentY += 10;
        
        activeFilters.forEach(([key, value]) => {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`${key}: ${value}`, 25, currentY);
            currentY += 10;
        });
        
        currentY += 10;
    }
    
    // Properties Table
    if (properties.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Properties Overview', 20, currentY);
        currentY += 15;
        
        const tableData = properties.map(property => [
            property.id.toString(),
            property.name,
            property.address,
            (property.totalAssets || 0).toString(),
            (property.passedTests || 0).toString(),
            (property.failedTests || 0).toString(),
            property.complianceRate ? `${property.complianceRate}%` : 'N/A'
        ]);
        
        doc.autoTable({
            startY: currentY,
            head: [['ID', 'Name', 'Address', 'Assets', 'Passed', 'Failed', 'Compliance']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [51, 115, 220], textColor: 255 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: 20, right: 20 },
            styles: { fontSize: 8 },
            columnStyles: {
                1: { cellWidth: 40 },
                2: { cellWidth: 50 }
            }
        });
    }
    
    // Add footer to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addPDFFooter(doc, i, totalPages);
    }
    
    // Download
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `cloudsafe_dashboard_report_${timestamp}.pdf`;
    doc.save(filename);
    
    return {
        success: true,
        filename,
        recordCount: properties.length
    };
};

/**
 * Export test results as branded PDF
 */
export const exportTestResultsPDF = (testResults, assetInfo = {}, propertyInfo = {}) => {
    const doc = new jsPDF();
    
    // Header
    const startY = addPDFHeader(
        doc, 
        'Test Results Report', 
        `${testResults.length} Test Records`
    );
    
    let currentY = startY + 10;
    
    // Summary by Status
    const statusCounts = testResults.reduce((acc, result) => {
        acc[result.status] = (acc[result.status] || 0) + 1;
        return acc;
    }, {});
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Test Status Summary', 20, currentY);
    currentY += 15;
    
    const statusData = Object.entries(statusCounts).map(([status, count]) => [
        status,
        count.toString(),
        `${Math.round((count / testResults.length) * 100)}%`
    ]);
    
    doc.autoTable({
        startY: currentY,
        head: [['Status', 'Count', 'Percentage']],
        body: statusData,
        theme: 'grid',
        headStyles: { fillColor: [51, 115, 220], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 20, right: 20 }
    });
    
    currentY = doc.lastAutoTable.finalY + 20;
    
    // Detailed Test Results
    if (testResults.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Detailed Test Results', 20, currentY);
        currentY += 15;
        
        const tableData = testResults.map(result => {
            const daysSinceTest = Math.floor((new Date() - new Date(result.testDate)) / (1000 * 60 * 60 * 24));
            return [
                result.referenceId,
                assetInfo[result.referenceId]?.name || 'Unknown',
                result.status,
                new Date(result.testDate).toLocaleDateString(),
                `${daysSinceTest} days`,
                result.pdfUrl ? 'Available' : 'None'
            ];
        });
        
        doc.autoTable({
            startY: currentY,
            head: [['Reference ID', 'Asset Name', 'Status', 'Test Date', 'Age', 'Report']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [51, 115, 220], textColor: 255 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: 20, right: 20 },
            styles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 35 },
                2: { cellWidth: 20 },
                3: { cellWidth: 25 },
                4: { cellWidth: 20 },
                5: { cellWidth: 20 }
            }
        });
    }
    
    // Add footer to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addPDFFooter(doc, i, totalPages);
    }
    
    // Download
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `cloudsafe_test_results_${timestamp}.pdf`;
    doc.save(filename);
    
    return {
        success: true,
        filename,
        recordCount: testResults.length
    };
};

/**
 * Export asset inventory as PDF
 */
export const exportAssetInventoryPDF = (assets, propertyInfo = {}) => {
    const doc = new jsPDF();
    
    // Header
    const startY = addPDFHeader(
        doc, 
        'Asset Inventory Report', 
        `${assets.length} Assets Across ${Object.keys(propertyInfo).length} Properties`
    );
    
    let currentY = startY + 10;
    
    // Assets by Type Summary
    const typeCounts = assets.reduce((acc, asset) => {
        acc[asset.type] = (acc[asset.type] || 0) + 1;
        return acc;
    }, {});
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Assets by Type', 20, currentY);
    currentY += 15;
    
    const typeData = Object.entries(typeCounts).map(([type, count]) => [
        type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        count.toString(),
        `${Math.round((count / assets.length) * 100)}%`
    ]);
    
    doc.autoTable({
        startY: currentY,
        head: [['Asset Type', 'Count', 'Percentage']],
        body: typeData,
        theme: 'grid',
        headStyles: { fillColor: [51, 115, 220], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 20, right: 20 }
    });
    
    currentY = doc.lastAutoTable.finalY + 20;
    
    // Detailed Asset List
    if (assets.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Asset Inventory', 20, currentY);
        currentY += 15;
        
        const tableData = assets.map(asset => [
            asset.referenceId || `${asset.propertyId}-${asset.type.toUpperCase()}-${asset.id}`,
            asset.name || `${asset.type} ${asset.id}`,
            asset.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            asset.location || 'Unknown',
            asset.status || 'Unknown',
            asset.lastTestDate ? new Date(asset.lastTestDate).toLocaleDateString() : 'Never'
        ]);
        
        doc.autoTable({
            startY: currentY,
            head: [['Reference ID', 'Name', 'Type', 'Location', 'Status', 'Last Test']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [51, 115, 220], textColor: 255 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: 20, right: 20 },
            styles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 30 },
                2: { cellWidth: 25 },
                3: { cellWidth: 30 },
                4: { cellWidth: 20 },
                5: { cellWidth: 25 }
            }
        });
    }
    
    // Add footer to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addPDFFooter(doc, i, totalPages);
    }
    
    // Download
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `cloudsafe_asset_inventory_${timestamp}.pdf`;
    doc.save(filename);
    
    return {
        success: true,
        filename,
        recordCount: assets.length
    };
};


